import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ContentFieldDto,
  DesignTestInputDto,
  DesignTestOutputDto
} from '../../../models/job-request-iterations-dto.interface';

interface ElectricalStabilityReportReading {
  temperature: number;
  voltage?: number;
}

interface XmlReading {
  element: Element;
  id: string | null;
  temperature?: number;
  voltages: number[];
}

@Component({
  selector: 'app-electrical-stability-test-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './electrical-stability-test-report.component.html',
  styleUrl: './electrical-stability-test-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ElectricalStabilityTestReportComponent {
  private static readonly DEFAULT_TEMPERATURES = [120, 150];

  @Input({ required: true }) test!: DesignTestInputDto;
  @Input({ required: true }) output!: DesignTestOutputDto;

  protected electricalStabilityReadings(): ElectricalStabilityReportReading[] {
    const inputReadings = this.xmlReadings(this.test.content, false);
    const outputReadings = this.xmlReadings(this.output.content, true);
    const outputById = new Map<string, XmlReading>();
    for (const reading of outputReadings) {
      if (reading.id) outputById.set(reading.id, reading);
    }

    const indices = this.readingIndices(inputReadings, outputReadings);
    return indices.map(index => {
      const inputReading = inputReadings[index - 1];
      const outputReading = (inputReading?.id ? outputById.get(inputReading.id) : undefined)
        ?? outputReadings[index - 1];
      const temperature = this.parsedNumber(
        [`esReading${index}Temperature`, index === 1 ? 'requestedTemperature' : ''],
        this.test.parsedInputValues
      )
        ?? inputReading?.temperature
        ?? ElectricalStabilityTestReportComponent.DEFAULT_TEMPERATURES[index - 1]
        ?? ElectricalStabilityTestReportComponent.DEFAULT_TEMPERATURES.at(-1)!;

      const parsedVoltages = [1, 2, 3]
        .map(voltageIndex => this.parsedNumber(
          [`esReading${index}Voltage${voltageIndex}`, index === 1 ? `voltage${voltageIndex}` : ''],
          this.output.parsedValues
        ))
        .filter((value): value is number => value !== undefined);
      const voltages = parsedVoltages.length > 0 ? parsedVoltages : (outputReading?.voltages ?? []);

      return {
        temperature,
        voltage: this.averageVoltage(voltages)
      };
    });
  }

  private readingIndices(inputReadings: XmlReading[], outputReadings: XmlReading[]): number[] {
    const indices = new Set<number>();
    for (const field of [...this.test.parsedInputValues, ...this.output.parsedValues]) {
      const match = /^esreading(\d+)(?:temperature|voltage\d+)$/.exec(this.normalize(field.label));
      if (match) indices.add(Number(match[1]));
    }

    const xmlCount = Math.max(inputReadings.length, outputReadings.length);
    for (let index = 1; index <= xmlCount; index++) indices.add(index);

    if (indices.size === 0) {
      ElectricalStabilityTestReportComponent.DEFAULT_TEMPERATURES.forEach((_, index) => indices.add(index + 1));
    }

    return Array.from(indices).sort((left, right) => left - right);
  }

  private xmlReadings(content: string | null, includeVoltages: boolean): XmlReading[] {
    if (!content) return [];

    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.querySelector('parsererror')) return [];

    const readingsContainer = Array.from(document.getElementsByTagName('*'))
      .find(element => this.normalize(element.localName) === 'readings');
    const readingElements = readingsContainer
      ? Array.from(readingsContainer.children)
      : [];

    return readingElements.map(element => ({
      element,
      id: this.xmlChildValue(element, 'id'),
      temperature: this.toNumber(this.xmlChildValue(element, 'temperature')),
      voltages: includeVoltages ? this.xmlVoltages(element) : []
    }));
  }

  private xmlVoltages(reading: Element): number[] {
    const voltageContainer = Array.from(reading.children)
      .find(element => this.normalize(element.localName).includes('dielectricbreakdownvoltages'));
    const voltageElements = voltageContainer
      ? Array.from(voltageContainer.children)
      : Array.from(reading.children).filter(element =>
          ['voltage', 'voltage1', 'voltage2', 'voltage3', 'dielectricbreakdownvoltage', 'int', 'int32']
            .includes(this.normalize(element.localName)));

    return voltageElements
      .map(element => this.toNumber(element.textContent?.trim()))
      .filter((value): value is number => value !== undefined)
      .slice(0, 3);
  }

  private xmlChildValue(element: Element, name: string): string | null {
    const child = Array.from(element.children)
      .find(candidate => this.normalize(candidate.localName) === name);
    return child?.textContent?.trim() || null;
  }

  private parsedNumber(names: string[], fields: ContentFieldDto[]): number | undefined {
    const acceptedNames = new Set(names.filter(Boolean).map(name => this.normalize(name)));
    const field = fields.find(item => acceptedNames.has(this.normalize(item.label)));
    return this.toNumber(field?.value);
  }

  private averageVoltage(values: number[]): number | undefined {
    if (values.length === 0) return undefined;
    return Math.trunc(values.reduce((sum, value) => sum + value, 0) / values.length);
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').replace(/[\s_\-/,().:]+/g, '').toLowerCase();
  }

  private toNumber(value: string | null | undefined): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}